import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import './NewsletterDetallePage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/newsletters/images/${sanitized}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const NewsletterDetallePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) {
      fetchNewsletter();
    }
  }, [slug]);

  const fetchNewsletter = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/newsletters/${slug}`));
      if (!response.ok) {
        if (response.status === 404) {
          setError('Newsletter no encontrado');
        } else {
          throw new Error('Error al cargar el newsletter');
        }
        return;
      }
      const data = await response.json();
      setNewsletter(data);
    } catch (err) {
      logError('[NewsletterDetallePage] Error fetching newsletter:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="newsletter-detail-page">
        <div className="newsletter-detail-container">
          <div className="newsletter-detail-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !newsletter) {
    return (
      <div className="newsletter-detail-page">
        <div className="newsletter-detail-container">
          <div className="newsletter-detail-error">
            <i className="bi-exclamation-triangle newsletter-error-icon" />
            <h2>Newsletter no encontrado</h2>
            <p>{error || 'El newsletter que buscas no existe o no está disponible.'}</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/newsletters')}
            >
              <i className="bi-arrow-left me-2" />
              Volver a newsletters
            </button>
          </div>
        </div>
      </div>
    );
  }

  const coverUrl = resolveImageUrl(newsletter.coverImage);
  const publishedDate = formatDate(newsletter.publishedAt || newsletter.createdAt);

  return (
    <div className="newsletter-detail-page">
      <div className="newsletter-detail-container">
        <button
          className="newsletter-detail-back"
          onClick={() => navigate('/newsletters')}
        >
          <i className="bi-arrow-left me-2" />
          Volver a newsletters
        </button>

        <article className="newsletter-detail-article">
          {coverUrl && (
            <div className="newsletter-detail-cover">
              <img src={coverUrl} alt={newsletter.title || 'Newsletter'} />
            </div>
          )}

          <header className="newsletter-detail-header">
            <div className="newsletter-detail-meta">
              <span className="newsletter-detail-date">
                <i className="bi-calendar3 me-1" />
                {publishedDate}
              </span>
            </div>
            <h1 className="newsletter-detail-title">{newsletter.title || 'Sin título'}</h1>
            {newsletter.shortDescription && (
              <p className="newsletter-detail-subtitle">{newsletter.shortDescription}</p>
            )}
          </header>

          {newsletter.contentHtml && (
            <div
              className="newsletter-detail-content"
              dangerouslySetInnerHTML={{ __html: newsletter.contentHtml }}
            />
          )}

          {!newsletter.contentHtml && (
            <div className="newsletter-detail-empty">
              <p>Este newsletter aún no tiene contenido disponible.</p>
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default NewsletterDetallePage;


